/**
 * The business's own Word templates, one per kind of document.
 *
 * Built so a ServiceM8 user can upload the .docx files they already have and
 * be told, straight away, whether anything on them will print empty. Preview
 * fills the template with the latest real document so what they approve is
 * what the customer gets (DOC-1).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, api, openDocument } from "../lib/api";
import type { DocumentKind, TemplateOut } from "../lib/types";
import { Card, Chip } from "./ui";

const KINDS: { kind: DocumentKind; label: string; builtIn: string }[] = [
  { kind: "quote", label: "Quote", builtIn: "Keystone's quote layout" },
  { kind: "invoice", label: "Invoice", builtIn: "Keystone's tax invoice layout" },
  { kind: "work_order", label: "Work order / docket", builtIn: "Keystone's service docket layout" },
];

export function DocumentSettings({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const templates = useQuery({ queryKey: ["templates"], queryFn: () => api.templates.list() });
  const [said, setSaid] = useState<{ tone: "info" | "crit" | "warn"; text: string } | null>(null);
  const [showFields, setShowFields] = useState(false);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["templates"] });
  const fail = (err: unknown) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Something went wrong." });

  const upload = useMutation({
    mutationFn: ({ kind, file }: { kind: DocumentKind; file: File }) => api.templates.upload(kind, file),
    onSuccess: (t) => {
      refresh();
      setSaid(
        t.unknown_fields.length
          ? {
              tone: "warn",
              text: `${t.name} is in use, but Keystone doesn't know ${t.unknown_fields.length} of its fields, so they'll print empty: ${t.unknown_fields.join(", ")}.`,
            }
          : { tone: "info", text: `${t.name} is in use. Every field on it is one Keystone can fill.` },
      );
    },
    onError: fail,
  });
  const toggle = useMutation({
    mutationFn: (t: TemplateOut) => (t.is_active ? api.templates.deactivate(t.id) : api.templates.activate(t.id)),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({ mutationFn: (id: string) => api.templates.remove(id), onSuccess: refresh, onError: fail });

  return (
    <Card title="Documents" subtitle="Use your own Word templates — ServiceM8 ones work as they are">
      {said && (
        <p className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"}>
          {said.text}
        </p>
      )}

      {KINDS.map(({ kind, label, builtIn }) => {
        const mine = (templates.data ?? []).filter((t) => t.kind === kind);
        const active = mine.find((t) => t.is_active);
        return (
          <div key={kind} className="doc-kind">
            <div className="doc-kind-head">
              <b>{label}</b>
              {active ? <Chip tone="ok">{active.name}</Chip> : <Chip tone="neutral">Built-in</Chip>}
            </div>
            {!active && <p className="tiny muted">Using {builtIn}.</p>}

            {mine.map((t) => (
              <div key={t.id} className="doc-row">
                <span className="mono tiny">{t.filename}</span>
                {t.unknown_fields.length > 0 && (
                  <span className="tiny text-crit"> · {t.unknown_fields.length} unknown field{t.unknown_fields.length === 1 ? "" : "s"}</span>
                )}
                <span className="doc-actions">
                  <button
                    className="linkish tiny"
                    type="button"
                    onClick={() => void openDocument(`/templates/${t.id}/preview`).catch(fail)}
                  >
                    Preview
                  </button>
                  {canEdit && (
                    <>
                      <button className="linkish tiny" type="button" onClick={() => toggle.mutate(t)}>
                        {t.is_active ? "Stop using" : "Use this"}
                      </button>
                      <button
                        className="linkish tiny"
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Delete ${t.filename}?`)) remove.mutate(t.id);
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </span>
              </div>
            ))}

            {canEdit && (
              <label className="btn" style={{ marginTop: 6 }}>
                {upload.isPending && upload.variables?.kind === kind ? "Uploading…" : active ? "Upload a new version" : "Upload a template"}
                <input
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) upload.mutate({ kind, file });
                    e.target.value = "";
                  }}
                />
              </label>
            )}
          </div>
        );
      })}

      <p className="tiny muted" style={{ marginTop: 12, lineHeight: 1.6 }}>
        In Word, add fields with <i>Mailings → Insert Merge Field</i>. A table row that uses a line field (
        <code>line.name</code>, or ServiceM8's <code>jobMaterial.name</code>) repeats once per line.{" "}
        <button className="linkish" type="button" onClick={() => setShowFields((v) => !v)}>
          {showFields ? "Hide the field list" : "Show every field"}
        </button>
      </p>
      {showFields && <FieldList />}
    </Card>
  );
}

function FieldList() {
  const fields = useQuery({ queryKey: ["template-fields"], queryFn: () => api.templates.fields() });
  if (!fields.data) return <p className="tiny muted">Loading…</p>;
  return (
    <div className="table-scroll">
      <table className="tiny">
        <thead>
          <tr>
            <th>Field</th>
            <th>Prints</th>
            <th>ServiceM8 name</th>
          </tr>
        </thead>
        <tbody>
          {fields.data.map((f) => (
            <tr key={f.name}>
              <td className="mono">{f.name}</td>
              <td>{f.means}</td>
              <td className="mono muted">{f.servicem8.join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
